import * as React from "react";
import { useMemo, useState } from "react";
import styled from "styled-components";
import upperFirst from "lodash/upperFirst";
import toNumber from "lodash/toNumber";
import toString from "lodash/toString";
import { useMantineTheme } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { Select } from "components/common";
import { MarkdownEditor, MarkdownPreview } from "components/common/MarkdownEditor";
import { Button, Col, ControlLabel, FormGroup, HelpBlock, Row, Input } from "components/bootstrap";
import EventDefinitionPriorityEnum from "logic/alerts/EventDefinitionPriorityEnum";
import usePluginEntities from "hooks/usePluginEntities";
import usePluggableLicenseCheck from "hooks/usePluggableLicenseCheck";
import * as FormsUtils from "util/FormsUtils";
import { getPathnameWithoutId } from "util/URLUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "logic/telemetry/Constants";
import EventSummaryTemplateHelp from "./EventSummaryTemplateHelp";
import { isSystemEventDefinition } from "../event-definitions-types";
import commonStyles from "../common/commonStyles.css";
const StyledRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacings.lg};

  & .form-group {
    width: 100%;
  }
`;
const InputContainer = styled.div`
  display: inline-block;
  position: relative;
  width: 100%;
`;
const InputFeedback = styled.div`
  position: absolute;
  right: 0;
  top: 24px;
  display: flex;
  align-items: center;
  min-height: 34px;
  padding-right: 3px;
`;
const priorityOptions = Object.entries(EventDefinitionPriorityEnum.properties).map(([key, value]) => ({
  value: key,
  label: upperFirst(value.name)
})).sort((a, b) => Number(b.value) - Number(a.value));
const EventDetailsForm = ({ eventDefinition, eventDefinitionEventProcedure, validation, onChange, canEdit }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h;
  const theme = useMantineTheme();
  const ltXl = useMediaQuery(`(min-width: ${theme.breakpoints.xl}`);
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const [showAddEventProcedureForm, setShowAddEventProcedureForm] = useState(false);
  const {
    data: { valid: validSecurityLicense }
  } = usePluggableLicenseCheck("/license/security");
  const readOnly = useMemo(
    () => !canEdit || isSystemEventDefinition(eventDefinition) || eventDefinition.config.type === "sigma-v1",
    [canEdit, eventDefinition]
  );
  const showEventProcedureSummary = useMemo(
    () => !!eventDefinitionEventProcedure && !showAddEventProcedureForm && validSecurityLicense,
    [eventDefinitionEventProcedure, showAddEventProcedureForm, validSecurityLicense]
  );
  const showAddNewEventProcedure = useMemo(
    () => !eventDefinitionEventProcedure && !showAddEventProcedureForm && !readOnly && validSecurityLicense,
    [eventDefinitionEventProcedure, showAddEventProcedureForm, readOnly, validSecurityLicense]
  );
  const handleChange = (event) => {
    const { name } = event.target;
    onChange(name, String(FormsUtils.getValueFromInput(event.target)));
  };
  const handlePriorityChange = (nextPriority) => {
    var _a2;
    sendTelemetry(TELEMETRY_EVENT_TYPE.EVENTDEFINITION_DETAILS.PRIORITY_CHANGED, {
      app_pathname: getPathnameWithoutId(pathname),
      app_section: "event-definition-details",
      app_action_value: "priority-select",
      priority: (_a2 = priorityOptions.find((opt) => opt.value === nextPriority)) == null ? void 0 : _a2.label
    });
    onChange("priority", toNumber(nextPriority));
  };
  const PluggableEventProcedureForm = (_b = (_a = usePluginEntities("views.components.eventProcedureForm")) == null ? void 0 : _a[0]) == null ? void 0 : _b.component;
  const PluggableEventProcedureSummary = (_d = (_c = usePluginEntities("views.components.eventProcedureSummary")) == null ? void 0 : _c[0]) == null ? void 0 : _d.component;
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { md: 12, lg: 6 }, /* @__PURE__ */ React.createElement("h2", { className: commonStyles.title }, "Event Details"), /* @__PURE__ */ React.createElement("fieldset", null, /* @__PURE__ */ React.createElement(StyledRow, null, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "event-definition-title",
      name: "title",
      label: "Title",
      type: "text",
      bsStyle: validation.errors.title ? "error" : null,
      help: (_g = (_f = (_e = validation == null ? void 0 : validation.errors) == null ? void 0 : _e.title) == null ? void 0 : _f[0]) != null ? _g : "Title for this Event Definition.",
      value: eventDefinition.title,
      onChange: handleChange,
      readOnly,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(FormGroup, { controlId: "event-definition-priority" }, /* @__PURE__ */ React.createElement(ControlLabel, null, "Priority"), /* @__PURE__ */ React.createElement(
    Select,
    {
      options: priorityOptions,
      value: toString(eventDefinition.priority),
      onChange: handlePriorityChange,
      clearable: false,
      disabled: readOnly,
      required: true
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Choose the priority for Events created from this Definition."))), /* @__PURE__ */ React.createElement(InputContainer, { className: "input-container" }, /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "event-definition-event-summary-template",
      name: "event_summary_template",
      label: "Event Summary Template",
      type: "text",
      help: "Template used to generate the Event and Alert summaries.",
      value: eventDefinition.event_summary_template,
      onChange: handleChange,
      readOnly
    }
  ), /* @__PURE__ */ React.createElement(InputFeedback, null, /* @__PURE__ */ React.createElement(EventSummaryTemplateHelp, null))), /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "event-definition-description",
      name: "description",
      label: /* @__PURE__ */ React.createElement("span", null, "Description ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")),
      type: "textarea",
      help: "Longer description for this Event Definition.",
      value: eventDefinition.description,
      onChange: handleChange,
      readOnly,
      rows: 2
    }
  ), !validSecurityLicense && /* @__PURE__ */ React.createElement(FormGroup, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Remediation Steps ", /* @__PURE__ */ React.createElement("small", { className: "text-muted" }, "(Optional)")), readOnly ? /* @__PURE__ */ React.createElement(
    MarkdownPreview,
    {
      show: true,
      withFullView: true,
      height: 150,
      value: eventDefinition.remediation_steps || "No remediation steps given"
    }
  ) : /* @__PURE__ */ React.createElement(
    MarkdownEditor,
    {
      id: "event-definition-remediation-steps",
      height: 150,
      value: (_h = eventDefinition.remediation_steps) != null ? _h : "",
      onChange: (newValue) => onChange("remediation_steps", newValue)
    }
  ), /* @__PURE__ */ React.createElement(HelpBlock, null, "Steps to remediate this Event. Markdown formatting is supported.")), validSecurityLicense && showAddEventProcedureForm && /* @__PURE__ */ React.createElement(
    PluggableEventProcedureForm,
    {
      eventProcedureId: eventDefinitionEventProcedure,
      remediationSteps: eventDefinition == null ? void 0 : eventDefinition.remediation_steps,
      onClose: () => setShowAddEventProcedureForm(false),
      onSave: (eventProcedureId) => {
        onChange("event_procedure", eventProcedureId);
        setShowAddEventProcedureForm(false);
      },
      onRemove: () => {
        onChange("event_procedure", null);
        setShowAddEventProcedureForm(false);
      }
    }
  ), showEventProcedureSummary && /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Event Procedure Summary"), /* @__PURE__ */ React.createElement(
    PluggableEventProcedureSummary,
    {
      eventProcedureId: eventDefinitionEventProcedure,
      canEdit: !readOnly,
      onEdit: () => setShowAddEventProcedureForm(true),
      onRemove: () => onChange("event_procedure", null),
      row: ltXl
    }
  )), showAddNewEventProcedure && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(ControlLabel, null, "Event Procedure Summary"), /* @__PURE__ */ React.createElement("p", null, "This Event Definition does not have any Event Procedures yet."), /* @__PURE__ */ React.createElement(Button, { bsStyle: "primary", onClick: () => setShowAddEventProcedureForm(true) }, "Add Event Procedure")))));
};
export default EventDetailsForm;
